import { useAuth } from '@teko/auth-react';
import { isServerless } from 'helpers';
import { useTenantConfig } from 'hooks';
import { useEffect, useLayoutEffect } from 'react';
import { useChatbotStore } from 'stores/chatbotStore';
import { isChatbotUserAllowed } from '../utils/accessControl';

const DEFAULT_SIDEBAR_WIDTH = 400;

const useChatbotConfig = (
  setIsEnabled: (v: boolean) => void,
  setSidebarConfig: (opts: {
    sidebarWidth: number;
    primaryColor?: string;
  }) => void,
) => {
  const { chatbotConfig } = useTenantConfig();
  const { user } = useAuth();

  useEffect(() => {
    const isAllowed = isChatbotUserAllowed(user, chatbotConfig?.accessControl);
    setIsEnabled(!!chatbotConfig && isServerless() && isAllowed);
    setSidebarConfig({
      sidebarWidth: chatbotConfig?.sidebarWidth ?? DEFAULT_SIDEBAR_WIDTH,
      primaryColor: chatbotConfig?.primaryColor,
    });
  }, [chatbotConfig, setIsEnabled, setSidebarConfig, user]);
};

// Computes the sidebar's top offset from the nav position. Initially the sidebar sits
// below the topbar + nav; when scrolling hides the topbar the offset is recomputed.
// The result is written to CSS variables consumed by the sidebar layout.
const useChatbotNavOffset = (enabled: boolean) => {
  useLayoutEffect(() => {
    if (!enabled) return;

    // shop-front's default layout exposes its nav as #mainNavigationBar. The tempi layout
    // has no such id — its header is the <header> inside #tempi-layout, which pins itself
    // while scrolling. Either way, anchor the sidebar to the header's bottom edge.
    const getNavEl = (): HTMLElement | null =>
      document.getElementById('mainNavigationBar') ??
      document.querySelector<HTMLElement>('#tempi-layout header');

    let navEl = getNavEl();
    const ro = new ResizeObserver(() => update());

    const update = () => {
      // The header may not be in the DOM yet when this effect runs; resolve it lazily.
      if (!navEl) navEl = getNavEl();
      if (!navEl) return;
      const navRect = navEl.getBoundingClientRect();
      const navHeight = navEl.offsetHeight;
      document.documentElement.style.setProperty(
        '--chatbot-nav-height',
        `${navHeight}px`,
      );
      // navRect.bottom is the header's bottom edge in viewport coords. Clamp to 0 so the
      // sidebar starts just below the header at the top of the page, then fills up as the
      // header pins / non-sticky parts scroll away — no blank white strip.
      const navBottomOffset = Math.max(0, navRect.bottom);
      document.documentElement.style.setProperty(
        '--chatbot-total-offset',
        `${navBottomOffset}px`,
      );
    };

    update();

    if (navEl) ro.observe(navEl);
    window.addEventListener('scroll', update, { passive: true });

    // The header can mount a tick after this effect runs; retry next frame to bind the
    // observer and recompute once it appears.
    let raf = 0;
    if (!navEl) {
      raf = requestAnimationFrame(() => {
        navEl = getNavEl();
        if (navEl) ro.observe(navEl);
        update();
      });
    }

    return () => {
      if (raf) cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('scroll', update);
    };
  }, [enabled]);
};

export const ChatbotStoreSync = (): null => {
  const { isEnabled, setIsEnabled, setSidebarConfig } = useChatbotStore();
  useChatbotConfig(setIsEnabled, setSidebarConfig);
  useChatbotNavOffset(isEnabled);
  return null;
};
